import { Departement } from '../models/models';

interface DeptScoped {
  id?: number;
  nom?: string | null;
  departement_id?: number | null;
}

export interface ScopeGroup<T extends DeptScoped> {
  /** null = items partagés (departement_id IS NULL) */
  dept:   Departement | null;
  /** items appartenant à ce dept (ou shared si dept=null) */
  items:  T[];
}

/**
 * Regroupe une liste d'items (depots / caisses / etc.) par leur `departement_id`.
 * - Items avec `departement_id` NULL → groupe "Partagé" (dept=null), toujours en tête
 * - Items rattachés à un dept de `departements` → groupe correspondant
 * - Items dont le dept n'existe plus dans `departements` → ignorés (cas dégradé)
 *
 * L'ordre des groupes dept suit l'ordre du tableau `departements` fourni.
 * Un groupe sans aucun item n'est pas inclus.
 *
 * Usage typique :
 *   const groups = groupByDept(depotData, departements);
 *   for (const g of groups) {
 *     // g.dept === null  → groupe "Partagé"
 *     // sinon            → g.dept.nom
 *     // g.items          → entries à afficher
 *   }
 */
export function groupByDept<T extends DeptScoped>(
  items:        T[],
  departements: Departement[]
): ScopeGroup<T>[] {
  const groups: ScopeGroup<T>[] = [];

  const sharedItems = items.filter(i => i.departement_id == null);
  if (sharedItems.length > 0) {
    groups.push({ dept: null, items: sharedItems });
  }

  for (const dept of departements) {
    const deptItems = items.filter(i => i.departement_id === dept.id);
    if (deptItems.length === 0) continue;
    groups.push({ dept, items: deptItems });
  }

  return groups;
}
